import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  styled,
  Avatar,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { Icons } from "../assets/icon";
import ImageUpload from "../common/ImageUpload";

// Gradient button for brand styling

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #8E2DE2 0%, #4A00E0 100%)",
  color: "var(--neutral-100)",
  font: "var(--semibold-md)",
  textTransform: "none",
  whiteSpace: "nowrap",
  borderRadius: "var(--spacing-3x)",
  padding: theme.spacing(1.4, 3),
  "&:hover": {
    background: "linear-gradient(90deg, #7329b3 0%, #3f00b2 100%)",
    boxShadow: "0 4px 14px rgba(138, 36, 235, 0.3)",
  },
}));

interface StepThreeProps {
  handleBack: () => void;
  isLoading: boolean;
  values: any;
  setFieldValue: (field: string, value: any) => void;
}

export const StepThree: React.FC<StepThreeProps> = ({
  handleBack,
  isLoading,
  values,
  setFieldValue,
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  // Handle image selection and preview
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    // Validate file type (allow only images)
    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/svg+xml",
    ];
    if (!validTypes.includes(file.type)) {
      enqueueSnackbar("Only image files (jpg, png, gif, svg) are allowed.", {
        variant: "warning",
      });
      return;
    }

    // Set value & preview
    setFieldValue("profile_image", file);
    setPreview(URL.createObjectURL(file));
  };

  // Handle cover image selection and preview
  const handleCoverImageChange = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    // Set value & preview
    setFieldValue("cover_image", file);
    setCoverPreview(URL.createObjectURL(file));
  };

  useEffect(() => {
    if (values.profile_image) {
      if (values.profile_image instanceof File) {
        setPreview(URL.createObjectURL(values.profile_image));
      } else if (typeof values.profile_image === "string") {
        setPreview(values.profile_image);
      }
    }

    if (values.cover_image) {
      if (values.cover_image instanceof File) {
        setCoverPreview(URL.createObjectURL(values.cover_image));
      } else if (typeof values.cover_image === "string") {
        setCoverPreview(values.cover_image);
      }
    }
  }, [values?.profile_image, values?.cover_image]);

  return (
    <>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Upload profile image
      </Typography>

      {/* Talent Profile Preview */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        {/* Profile Picture Preview */}
        <Avatar
          src={preview || values.profile_image}
          sx={{ width: { xs: 48, sm: 64 }, height: { xs: 48, sm: 64 } }}
        />

        <Box>
          {/* Talent Name */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {values.username || "Talent Name"}{" "}
            <Typography component="span" sx={{ color: "#666" }}>
              @TalentName
            </Typography>
          </Typography>

          {/* Selected Skills from Step 2 */}
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
            {values.skills.map((sk: string, idx: number) => (
              <Chip
                key={`${sk}-${idx}`}
                label={sk}
                sx={{
                  fontSize: "14px",
                  height: { xs: "24px", sm: "32px" },
                  "& .MuiChip-label": {
                    px: { xs: 1, sm: 1.5 },
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      </Stack>

      {/* Profile Image Upload */}
      <Box
        sx={{
          border: "2px dashed var(--primary-300)",
          borderRadius: 3,
          p: { xs: 2, sm: 4 },
          textAlign: "center",
          mb: 4,
          backgroundColor: "var(--neutral-50)",
          cursor: "pointer",
          "&:hover": { backgroundColor: "var(--neutral-100)" },
        }}
        onClick={() => document.getElementById("upload-image")?.click()}
      >
        <Typography
          variant="body1"
          sx={{ mb: 1, fontWeight: 600, fontSize: { xs: "14px", sm: "16px" } }}
        >
          Drag and drop profile image to upload
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 2, fontSize: { xs: "12px", sm: "14px" } }}
        >
          SVG, PNG, JPG or GIF (max. 800×400px)
        </Typography>

        <div
          style={{
            borderRadius: "12px",
            color: "var(--neutral-900)",
            border: "1px solid var(--neutral-200)",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "fit-content",
            margin: "0 auto",
          }}
        >
          <Icons.ImageUserPlus height={20} width={20} />
          <Typography variant="body2" sx={{ font: "var(--semibold-sm)" }}>
            Browse Profile Image
          </Typography>
        </div>

        {/* Hidden input for profile image */}
        <input
          accept="image/*"
          type="file"
          id="upload-image"
          style={{ display: "none" }}
          onChange={handleImageChange}
        />
      </Box>

      {/* Cover Image Upload */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, mb: 3, fontSize: { xs: "18px", sm: "24px" } }}
      >
        Upload cover image
      </Typography>

      {/* Cover Image Preview */}
      {coverPreview && (
        <Box
          sx={{
            mb: 3,
            width: "100%",
            height: { xs: "150px", sm: "200px" },
            position: "relative",
          }}
        >
          <img
            src={coverPreview}
            alt="Cover preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "12px",
            }}
          />
        </Box>
      )}

      <ImageUpload
        onFileSelect={handleCoverImageChange}
        name="cover_image"
        label="Drag and drop cover image to upload"
        subLabel="SVG, PNG, JPG or GIF (max. 1920×1080px)"
        maxFiles={1}
        layout="column"
        browseButtonLabel="Browse Cover Image"
      />

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          mt: 3,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{
            width: { xs: "100%", sm: 180 },
            font: { xs: "var(--semibold-sm)", sm: "var(--semibold-md)" },
            color: "var(--neutral-900)",
            height: "44px",
            borderRadius: "var(--spacing-3x)",
          }}
        >
          Previous
        </Button>
        <GradientButton
          type="submit"
          disabled={isLoading}
          sx={{
            width: { xs: "100%", sm: 180 },
            height: "44px",
            font: { xs: "var(--semibold-sm)", sm: "var(--semibold-md)" },
          }}
        >
          {isLoading ? (
            <CircularProgress size="24px" color="inherit" />
          ) : (
            "Continue to TaaS"
          )}
        </GradientButton>
      </Box>
    </>
  );
};
